import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, MapPin, ArrowRight, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * GA Event Tracking helper
 * Uses gtag if available, otherwise logs to console
 */
const trackEvent = (eventName, params) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  } else {
    console.log('GA Event:', eventName, params);
  }
};

/**
 * UpcomingEventsTeaser - Shows upcoming events from the Events Hub
 * 
 * Props:
 * - title: string (default: "Upcoming major events")
 * - maxItems: number (default: 3)
 * - context: string - for GA tracking (e.g., "venue-finding-service", "group-accommodation-service", "homepage")
 */
export default function UpcomingEventsTeaser({ 
  title = "Upcoming major events", 
  maxItems = 3,
  context = "general"
}) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/api/public/events-upcoming?limit=${maxItems}`);
        setEvents(response.data.events || []);
      } catch (err) {
        console.error('Error fetching upcoming events:', err);
        setError('Unable to load events');
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [maxItems]);

  const handleEventClick = (event) => {
    trackEvent('events_hub_teaser_click', {
      context: context,
      event_name: event.name,
      city: event.citySlug,
      event_slug: event.slug
    });
  };

  // Don't render anything if no events or error
  if (loading) {
    return (
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
            <span className="ml-2 text-gray-600">Loading events...</span>
          </div>
        </div>
      </section>
    );
  }

  if (error || events.length === 0) {
    // Render nothing or minimal placeholder
    return null;
  }

  return (
    <section className="py-12 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{title}</h2>
          <p className="text-gray-600">
            Group accommodation & meeting rooms available for teams attending these events
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => (
            <Link
              key={event.slug}
              to={`/events/${event.citySlug}/${event.venueSlug}/${event.slug}`}
              onClick={() => handleEventClick(event)}
              className="block group"
              data-testid={`teaser-event-${event.slug}`}
            >
              <Card className="h-full bg-white border border-gray-200 hover:border-orange-500 hover:shadow-lg transition-all duration-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold text-gray-900 group-hover:text-orange-600 transition-colors line-clamp-2">
                    {event.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-orange-500 flex-shrink-0" />
                      <span className="truncate">{event.city}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="w-4 h-4 mr-2 text-orange-500 flex-shrink-0" />
                      <span>{event.dates?.display}</span>
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-500 mb-3 line-clamp-2">
                    {event.shortDescription}
                  </p>
                  
                  <div className="flex items-center text-sm font-medium text-orange-600 group-hover:text-orange-700">
                    View event & accommodation options
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Link to full Events Hub */}
        <div className="text-center mt-8">
          <Link
            to="/events"
            onClick={() => trackEvent('events_hub_teaser_click', { context, event_name: 'view_all', city: 'all', event_slug: 'hub' })}
            className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium"
          >
            View all events in the Events Hub
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </div>
    </section>
  );
}
